import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useProduct, useTestRuns, useLatestGates, useLighthouseScores, useSecurityScans, useDeploys, useHealthHistory, useGateHistory } from '@/hooks/use-products';
import { HealthRing } from '@/components/ui/health-ring';
import { MaturityBadge } from '@/components/ui/maturity-badge';
import { MetricCard } from '@/components/ui/metric-card';
import { healthTextClass, barColorClass, statusDotClass, relativeTime, formatDuration, formatNumber } from '@/lib/format';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  BarChart, Bar, Cell,
} from 'recharts';

type TabKey = 'overview' | 'tests' | 'lighthouse' | 'security' | 'deploys' | 'gates';

const TABS: { key: TabKey; label: string }[] = [
  { key: 'overview', label: 'Overview' },
  { key: 'tests', label: 'Tests' },
  { key: 'lighthouse', label: 'Lighthouse' },
  { key: 'security', label: 'Security' },
  { key: 'deploys', label: 'Deploys' },
  { key: 'gates', label: 'Gates' },
];

const DIMENSION_META: Record<string, { name: string; weight: number }> = {
  test_pass_rate: { name: 'Test Pass Rate', weight: 20 },
  security: { name: 'Security', weight: 15 },
  accessibility: { name: 'Accessibility', weight: 12 },
  deploy_freshness: { name: 'Deploy Freshness', weight: 10 },
  gate_health: { name: 'Gate Health', weight: 10 },
  code_coverage: { name: 'Code Coverage', weight: 10 },
  performance: { name: 'Performance', weight: 8 },
  cross_browser: { name: 'Cross-Browser', weight: 8 },
  bundle_size: { name: 'Bundle Size', weight: 7 },
};

const CHART_TOOLTIP_STYLE = {
  contentStyle: { background: '#161616', border: '1px solid #1E1E1E', borderRadius: 6, fontSize: '0.78rem' },
  labelStyle: { color: '#A0A0A0' },
};

const CHART_GRID = { strokeDasharray: '3 3', stroke: 'rgba(255,255,255,0.04)' };
const AXIS_TICK = { fill: '#A0A0A0', fontSize: 11 };

export function ProductDetailPage() {
  const { slug = '' } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('overview');

  const { data: product, isLoading } = useProduct(slug);
  const { data: testRuns } = useTestRuns(slug);
  const { data: gates } = useLatestGates(slug);
  const { data: lighthouse } = useLighthouseScores(slug);
  const { data: securityScans } = useSecurityScans(slug);
  const { data: deploys } = useDeploys(slug);
  const { data: healthHistory } = useHealthHistory(slug);
  const { data: gateHistory } = useGateHistory(slug);

  if (isLoading) {
    return (
      <div>
        <div className="h-7 w-48 bg-bg-elevated rounded animate-pulse mb-6" />
        <div className="h-32 bg-bg-elevated rounded animate-pulse" />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="text-center py-12 text-text-muted">
        <h3 className="text-base text-text-secondary mb-1">Product not found</h3>
        <button onClick={() => navigate('/')} className="text-accent-bright text-sm hover:underline mt-2">
          &larr; Back to Ecosystem
        </button>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center gap-5 mb-6 pb-6 border-b border-border">
        <button
          onClick={() => navigate('/')}
          className="text-[0.78rem] text-text-muted hover:text-accent-bright transition-colors"
        >
          &larr; Back
        </button>
        <div>
          <span className="font-display text-xl font-bold">{product.name}</span>
          <MaturityBadge level={product.maturity_level} className="ml-2" />
        </div>
        <div className="ml-auto flex items-center gap-3">
          <div>
            <div className={`font-display text-3xl font-bold ${healthTextClass(product.health_score)}`}>
              {product.health_score ?? '—'}
            </div>
            <div className="text-[0.72rem] text-text-muted">Health Score</div>
          </div>
          <HealthRing score={product.health_score} size={64} showLabel={false} />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-0 border-b border-border mb-8">
        {TABS.map(tab => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`px-4 py-2.5 text-[0.78rem] font-semibold transition-all -mb-px border-b-2 ${
              activeTab === tab.key
                ? 'text-accent-bright border-accent'
                : 'text-text-muted border-transparent hover:text-text-secondary'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <OverviewTab
          product={product}
          healthHistory={healthHistory}
          testRuns={testRuns}
          securityScans={securityScans}
          lighthouse={lighthouse}
          deploys={deploys}
          gates={gates}
        />
      )}
      {activeTab === 'tests' && <TestsTab testRuns={testRuns} />}
      {activeTab === 'lighthouse' && <LighthouseTab scores={lighthouse} />}
      {activeTab === 'security' && <SecurityTab scans={securityScans} />}
      {activeTab === 'deploys' && <DeploysTab deploys={deploys} />}
      {activeTab === 'gates' && <GatesTab gates={gates} gateHistory={gateHistory} />}
    </div>
  );
}

/* ─── Overview Tab ─── */
function computeLiveDimensions(
  testRuns?: import('@/lib/types').TestRun[],
  securityScans?: import('@/lib/types').SecurityScan[],
  lighthouse?: import('@/lib/types').LighthouseScore[],
  deploys?: import('@/lib/types').Deploy[],
  gates?: import('@/lib/types').GateResult[],
): Record<string, number | null> {
  const scores: Record<string, number | null> = {};

  const latestRun = testRuns?.[0];
  if (latestRun && latestRun.total_tests > 0) {
    scores.test_pass_rate = Math.round((latestRun.total_passed / latestRun.total_tests) * 1000) / 10;
  }

  const latestScan = securityScans?.[0];
  if (latestScan) {
    let s = 100;
    s -= latestScan.npm_prod_critical * 25;
    s -= latestScan.npm_prod_high * 15;
    s -= latestScan.npm_prod_moderate * 5;
    s -= latestScan.gitleaks_findings * 30;
    if (latestScan.owasp_total > 0) {
      s -= ((latestScan.owasp_total - latestScan.owasp_passed) / latestScan.owasp_total) * 30;
    }
    scores.security = Math.max(0, Math.min(100, Math.round(s * 10) / 10));
  }

  if (lighthouse && lighthouse.length > 0) {
    const avgA11y = lighthouse.reduce((sum, l) => sum + l.accessibility, 0) / lighthouse.length;
    scores.accessibility = Math.round(avgA11y * 1000) / 10;
    const avgPerf = lighthouse.reduce((sum, l) => sum + l.performance, 0) / lighthouse.length;
    scores.performance = Math.round(avgPerf * 1000) / 10;
  }

  const successfulDeploys = deploys?.filter(d => d.success) ?? [];
  if (successfulDeploys.length > 0) {
    const latestDeploy = successfulDeploys[0];
    const daysSince = (Date.now() - new Date(latestDeploy.deploy_timestamp).getTime()) / 86_400_000;
    if (daysSince <= 1) scores.deploy_freshness = 100;
    else if (daysSince <= 3) scores.deploy_freshness = 90;
    else if (daysSince <= 7) scores.deploy_freshness = 75;
    else if (daysSince <= 14) scores.deploy_freshness = 50;
    else if (daysSince <= 30) scores.deploy_freshness = 25;
    else scores.deploy_freshness = 0;
  }

  if (gates && gates.length > 0) {
    const passed = gates.filter(g => g.status === 'pass' || g.status === 'warn').length;
    scores.gate_health = Math.round((passed / gates.length) * 1000) / 10;
  }

  return scores;
}

function OverviewTab({ product, healthHistory, testRuns, securityScans, lighthouse, deploys, gates }: {
  product: import('@/lib/types').Product;
  healthHistory?: import('@/lib/types').HealthSnapshot[];
  testRuns?: import('@/lib/types').TestRun[];
  securityScans?: import('@/lib/types').SecurityScan[];
  lighthouse?: import('@/lib/types').LighthouseScore[];
  deploys?: import('@/lib/types').Deploy[];
  gates?: import('@/lib/types').GateResult[];
}) {
  const [expandedDim, setExpandedDim] = useState<string | null>(null);

  const liveScores = computeLiveDimensions(testRuns, securityScans, lighthouse, deploys, gates);
  const dimensions = Object.entries(DIMENSION_META).map(([key, meta]) => ({
    key,
    ...meta,
    score: liveScores[key] ?? null,
  }));

  const trendData = healthHistory?.map((h, i) => ({
    day: `Day ${i + 1}`,
    score: h.health_score,
  })) ?? [];

  return (
    <div>
      {/* Dimension Cards */}
      <div className="grid grid-cols-3 gap-4 mb-8 max-[768px]:grid-cols-2 max-[480px]:grid-cols-1">
        {dimensions.map(d => (
          <div key={d.key}>
            <div
              className={`bg-bg-card border rounded-md p-4 cursor-pointer transition-all ${
                expandedDim === d.key
                  ? 'border-accent bg-[rgba(0,144,144,0.12)]'
                  : 'border-border hover:border-text-muted hover:bg-bg-elevated'
              }`}
              onClick={() => setExpandedDim(expandedDim === d.key ? null : d.key)}
              role="button"
              tabIndex={0}
              aria-expanded={expandedDim === d.key}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setExpandedDim(expandedDim === d.key ? null : d.key); }}
            >
              <div className="flex items-center justify-between mb-2.5">
                <span className="text-[0.78rem] font-semibold">{d.name}</span>
                <span className="text-[0.62rem] text-text-muted font-mono">{d.weight}%</span>
              </div>
              <div className="h-1.5 bg-bg-elevated rounded-full overflow-hidden mb-2">
                <div
                  className={`h-full rounded-full transition-[width] duration-700 ease-out ${d.score !== null ? barColorClass(d.score) : 'bg-text-muted'}`}
                  style={{ width: `${d.score ?? 0}%` }}
                />
              </div>
              <div className="flex justify-between items-baseline">
                <span className={`font-mono text-[0.82rem] font-semibold ${healthTextClass(d.score)}`}>
                  {d.score !== null ? `${d.score}%` : '—'}
                </span>
                <span className="text-[0.68rem] text-text-muted">{d.score !== null ? (d.score >= 85 ? 'Healthy' : d.score >= 70 ? 'Needs attention' : 'Critical') : 'No data'}</span>
              </div>
            </div>

            {expandedDim === d.key && (
              <div className="mt-2 bg-bg-card border border-[rgba(0,144,144,0.25)] rounded-md px-5 py-4 animate-[fadeSlideIn_0.15s_ease-out]">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-[0.88rem] font-semibold">{d.name}</h4>
                  <span className={`font-mono text-[0.88rem] font-bold ${healthTextClass(d.score)}`}>
                    {d.score !== null ? `${d.score}%` : '—'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-[0.76rem]">
                  <dt className="text-text-muted font-medium">Weight</dt>
                  <dd className="font-medium">{d.weight}%</dd>
                  <dt className="text-text-muted font-medium">Status</dt>
                  <dd className={`font-medium ${healthTextClass(d.score)}`}>{d.score !== null ? (d.score >= 85 ? 'Healthy' : d.score >= 70 ? 'Needs attention' : 'Critical') : 'Awaiting data'}</dd>
                  <dt className="text-text-muted font-medium">Contribution</dt>
                  <dd className="font-medium">{d.score !== null ? `${((d.score * d.weight) / 100).toFixed(1)} pts` : '—'}</dd>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Error Budget Indicator */}
      <ErrorBudget product={product} healthHistory={healthHistory} />

      {/* Health Trend Chart */}
      <div className="bg-bg-card border border-border rounded-lg p-5 mb-6">
        <h4 className="text-[0.85rem] font-semibold mb-4">Health Score — Last 30 Days</h4>
        <div className="h-[220px]">
          {trendData.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData}>
                <CartesianGrid {...CHART_GRID} />
                <XAxis dataKey="day" hide />
                <YAxis domain={[0, 100]} tick={AXIS_TICK} tickLine={false} axisLine={false} />
                <Tooltip {...CHART_TOOLTIP_STYLE} formatter={(value) => [`${value}`, 'Health']} />
                <Line type="monotone" dataKey="score" stroke="#009090" strokeWidth={2} dot={false} fill="rgba(0,144,144,0.08)" />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-text-muted text-sm">
              No trend data yet. Health snapshots are captured daily.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/* ─── Error Budget ─── */
function ErrorBudget({ product, healthHistory }: { product: import('@/lib/types').Product; healthHistory?: import('@/lib/types').HealthSnapshot[] }) {
  const health = product.health_score ?? 0;
  const tierThreshold =
    product.maturity_level === 'gold' ? 90
    : product.maturity_level === 'silver' ? 75
    : product.maturity_level === 'bronze' ? 50
    : 0;

  const budget = health - tierThreshold;

  // 7-day trend delta
  let weekDelta: number | null = null;
  if (healthHistory && healthHistory.length >= 2) {
    const recent = healthHistory.slice(0, 7);
    if (recent.length >= 2) {
      weekDelta = recent[0].health_score - recent[recent.length - 1].health_score;
    }
  }

  if (tierThreshold === 0) return null;

  const budgetColor = budget <= 0 ? 'text-danger' : budget <= 5 ? 'text-warning' : 'text-success';
  const budgetBg = budget <= 0 ? 'bg-danger-soft border-danger/20' : budget <= 5 ? 'bg-warning-soft border-warning/20' : 'bg-bg-card border-border';

  return (
    <div className={`${budgetBg} border rounded-md p-4 mb-6 flex items-center gap-4`}>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <span className="text-[0.78rem] font-semibold">Health Budget</span>
          {budget <= 0 && (
            <span className="text-[0.62rem] font-bold uppercase tracking-wider bg-danger/15 text-danger px-2 py-0.5 rounded-full">
              Quality freeze recommended
            </span>
          )}
          {budget > 0 && budget <= 5 && (
            <span className="text-[0.62rem] font-bold uppercase tracking-wider bg-warning/15 text-warning px-2 py-0.5 rounded-full">
              Budget low
            </span>
          )}
        </div>
        <p className="text-[0.72rem] text-text-muted">
          {budget > 0
            ? `${budget.toFixed(1)} points above ${product.maturity_level} threshold (${tierThreshold}).`
            : `Score has dropped below the ${product.maturity_level} threshold (${tierThreshold}). Focus on quality recovery.`}
          {weekDelta !== null && (
            <span className={weekDelta >= 0 ? 'text-success' : 'text-danger'}>
              {' '}{weekDelta >= 0 ? '▲' : '▼'} {Math.abs(weekDelta).toFixed(1)} this week
            </span>
          )}
        </p>
      </div>
      <div className="text-right shrink-0">
        <div className={`font-display text-2xl font-bold ${budgetColor}`}>
          {budget > 0 ? `+${budget.toFixed(1)}` : budget.toFixed(1)}
        </div>
        <div className="text-[0.62rem] text-text-muted">points remaining</div>
      </div>
    </div>
  );
}

/* ─── Tests Tab ─── */
function TestsTab({ testRuns }: { testRuns?: import('@/lib/types').TestRun[] }) {
  const latest = testRuns?.[0];
  const passRate = latest && latest.total_tests > 0
    ? Math.round((latest.total_passed / latest.total_tests) * 100)
    : null;

  const chartData = testRuns
    ? [...testRuns].reverse().map((r, i) => ({
        label: r.run_id.slice(0, 7),
        tests: r.total_tests,
        index: i,
      }))
    : [];

  return (
    <div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4 mb-8">
        <MetricCard label="Total Tests" value={latest ? formatNumber(latest.total_tests) : '—'} />
        <MetricCard label="Pass Rate" value={passRate !== null ? `${passRate}%` : '—'} valueClassName={passRate !== null && passRate === 100 ? 'text-success' : ''} />
        <MetricCard label="Last Run" value={latest ? relativeTime(latest.run_timestamp) : '—'} valueClassName="!text-base" />
        <MetricCard label="Run Duration" value={latest ? formatDuration(latest.duration_ms) : '—'} valueClassName="!text-base" />
      </div>

      {/* Test Count Over Time Chart */}
      {chartData.length > 1 && (
        <div className="bg-bg-card border border-border rounded-lg p-5 mb-6">
          <h4 className="text-[0.85rem] font-semibold mb-4">Test Count Over Time</h4>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid {...CHART_GRID} />
                <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={false} />
                <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} />
                <Tooltip {...CHART_TOOLTIP_STYLE} formatter={(value) => [formatNumber(value as number), 'Tests']} />
                <Bar dataKey="tests" fill="rgba(0,144,144,0.5)" stroke="#009090" strokeWidth={1} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="bg-bg-card border border-border rounded-lg p-5">
        <h4 className="text-[0.85rem] font-semibold mb-4">Recent Runs</h4>
        {testRuns && testRuns.length > 0 ? (
          <table className="w-full border-collapse text-[0.78rem]">
            <thead>
              <tr>
                {['Run', 'Branch', 'Tests', 'Passed', 'Failed', 'Gates', 'Duration', 'When'].map(h => (
                  <th key={h} className="text-left text-[0.62rem] font-semibold uppercase tracking-wider text-text-muted px-3 py-2.5 border-b border-border">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {testRuns.map(run => (
                <tr key={run.id} className="hover:bg-bg-elevated">
                  <td className="px-3 py-2.5 border-b border-border-subtle text-text-secondary">
                    <span className={`inline-block w-2 h-2 rounded-full mr-1.5 align-middle ${run.total_failed > 0 ? 'bg-warning' : 'bg-success'}`} />
                    <span className="font-mono text-[0.72rem]">{run.run_id.slice(0, 9)}</span>
                  </td>
                  <td className="px-3 py-2.5 border-b border-border-subtle font-mono text-[0.72rem] text-text-secondary">{run.branch || '—'}</td>
                  <td className="px-3 py-2.5 border-b border-border-subtle text-text-secondary">{formatNumber(run.total_tests)}</td>
                  <td className="px-3 py-2.5 border-b border-border-subtle text-success">{formatNumber(run.total_passed)}</td>
                  <td className={`px-3 py-2.5 border-b border-border-subtle ${run.total_failed > 0 ? 'text-danger' : 'text-text-secondary'}`}>{run.total_failed}</td>
                  <td className="px-3 py-2.5 border-b border-border-subtle text-text-secondary">{run.gate_passed}/{run.gate_total}</td>
                  <td className="px-3 py-2.5 border-b border-border-subtle text-text-secondary">{formatDuration(run.duration_ms)}</td>
                  <td className="px-3 py-2.5 border-b border-border-subtle text-text-secondary">{relativeTime(run.run_timestamp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-8 text-text-muted text-sm">No test runs recorded yet.</div>
        )}
      </div>
    </div>
  );
}

/* ─── Lighthouse Tab ─── */
function LighthouseTab({ scores }: { scores?: import('@/lib/types').LighthouseScore[] }) {
  const latest = scores?.[0];

  const chartData = scores
    ? [...scores].reverse().map((s) => ({
        label: new Date(s.run_timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        Performance: Math.round(s.performance * 100),
        Accessibility: Math.round(s.accessibility * 100),
        'Best Practices': Math.round(s.best_practices * 100),
        SEO: Math.round(s.seo * 100),
      }))
    : [];

  return (
    <div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4 mb-8">
        <MetricCard label="Performance" value={latest ? Math.round(latest.performance * 100) : '—'} valueClassName={latest && latest.performance >= 0.85 ? 'text-success' : latest && latest.performance >= 0.7 ? 'text-warning' : ''} />
        <MetricCard label="Accessibility" value={latest ? Math.round(latest.accessibility * 100) : '—'} valueClassName={latest && latest.accessibility >= 0.95 ? 'text-success' : ''} />
        <MetricCard label="Best Practices" value={latest ? Math.round(latest.best_practices * 100) : '—'} valueClassName={latest && latest.best_practices >= 0.9 ? 'text-success' : ''} />
        <MetricCard label="SEO" value={latest ? Math.round(latest.seo * 100) : '—'} valueClassName={latest && latest.seo >= 0.9 ? 'text-success' : ''} />
      </div>

      {/* Lighthouse Scores Over Time Chart */}
      {chartData.length > 1 && (
        <div className="bg-bg-card border border-border rounded-lg p-5 mb-6">
          <h4 className="text-[0.85rem] font-semibold mb-4">Lighthouse Scores Over Time</h4>
          <div className="h-[240px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData}>
                <CartesianGrid {...CHART_GRID} />
                <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={false} />
                <YAxis domain={[0, 100]} tick={AXIS_TICK} tickLine={false} axisLine={false} />
                <Tooltip {...CHART_TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ fontSize: '0.72rem' }} iconSize={12} />
                <Line type="monotone" dataKey="Accessibility" stroke="#22C55E" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="Best Practices" stroke="#00ADAD" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="SEO" stroke="#3B82F6" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="Performance" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="bg-bg-card border border-border rounded-lg p-5">
        <h4 className="text-[0.85rem] font-semibold mb-4">Scores By Route</h4>
        {scores && scores.length > 0 ? (
          <table className="w-full border-collapse text-[0.78rem]">
            <thead>
              <tr>
                {['Route', 'Performance', 'Accessibility', 'Best Practices', 'SEO', 'Build'].map(h => (
                  <th key={h} className="text-left text-[0.62rem] font-semibold uppercase tracking-wider text-text-muted px-3 py-2.5 border-b border-border">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {scores.map(s => (
                <tr key={s.id} className="hover:bg-bg-elevated">
                  <td className="px-3 py-2.5 border-b border-border-subtle font-mono text-[0.72rem] text-text-secondary">{s.url}</td>
                  <td className={`px-3 py-2.5 border-b border-border-subtle ${s.performance >= 0.85 ? 'text-success' : s.performance >= 0.7 ? 'text-warning' : 'text-danger'}`}>{Math.round(s.performance * 100)}</td>
                  <td className={`px-3 py-2.5 border-b border-border-subtle ${s.accessibility >= 0.95 ? 'text-success' : 'text-warning'}`}>{Math.round(s.accessibility * 100)}</td>
                  <td className={`px-3 py-2.5 border-b border-border-subtle ${s.best_practices >= 0.9 ? 'text-success' : 'text-warning'}`}>{Math.round(s.best_practices * 100)}</td>
                  <td className={`px-3 py-2.5 border-b border-border-subtle ${s.seo >= 0.9 ? 'text-success' : 'text-warning'}`}>{Math.round(s.seo * 100)}</td>
                  <td className="px-3 py-2.5 border-b border-border-subtle text-text-secondary capitalize">{s.build_type}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-8 text-text-muted text-sm">No Lighthouse scores recorded yet.</div>
        )}
      </div>
    </div>
  );
}

/* ─── Security Tab ─── */
function SecurityTab({ scans }: { scans?: import('@/lib/types').SecurityScan[] }) {
  const latest = scans?.[0];

  const trendData = scans && scans.length > 1
    ? [...scans].reverse().map((s) => ({
        label: new Date(s.run_timestamp).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
        'Prod Critical': s.npm_prod_critical,
        'Prod High': s.npm_prod_high,
        'Dev High': s.npm_dev_high,
      }))
    : [];

  return (
    <div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-4 mb-8">
        <div className="bg-bg-card border border-border rounded-md p-4 text-center">
          <div className={`font-display text-[1.75rem] font-bold ${latest && latest.npm_prod_critical === 0 ? 'text-success' : 'text-danger'}`}>
            {latest?.npm_prod_critical ?? '—'}
          </div>
          <div className="text-[0.68rem] text-text-muted mt-0.5">Prod Critical</div>
        </div>
        <div className="bg-bg-card border border-border rounded-md p-4 text-center">
          <div className={`font-display text-[1.75rem] font-bold ${latest && latest.npm_prod_high === 0 ? 'text-success' : 'text-danger'}`}>
            {latest?.npm_prod_high ?? '—'}
          </div>
          <div className="text-[0.68rem] text-text-muted mt-0.5">Prod High</div>
        </div>
        <div className="bg-bg-card border border-border rounded-md p-4 text-center">
          <div className={`font-display text-[1.75rem] font-bold ${latest && latest.eslint_errors === 0 ? 'text-success' : 'text-danger'}`}>
            {latest?.eslint_errors ?? '—'}
          </div>
          <div className="text-[0.68rem] text-text-muted mt-0.5">ESLint Security</div>
        </div>
        <div className="bg-bg-card border border-border rounded-md p-4 text-center">
          <div className={`font-display text-[1.75rem] font-bold ${latest && latest.owasp_passed === latest.owasp_total ? 'text-success' : 'text-warning'}`}>
            {latest ? `${latest.owasp_passed}/${latest.owasp_total}` : '—'}
          </div>
          <div className="text-[0.68rem] text-text-muted mt-0.5">OWASP Passed</div>
        </div>
        <div className="bg-bg-card border border-border rounded-md p-4 text-center">
          <div className={`font-display text-[1.75rem] font-bold ${latest && latest.gitleaks_findings === 0 ? 'text-success' : 'text-danger'}`}>
            {latest?.gitleaks_findings ?? '—'}
          </div>
          <div className="text-[0.68rem] text-text-muted mt-0.5">Secrets Found</div>
        </div>
      </div>

      {/* Vulnerability Trend Chart */}
      {trendData.length > 1 && (
        <div className="bg-bg-card border border-border rounded-lg p-5 mb-6">
          <h4 className="text-[0.85rem] font-semibold mb-4">Vulnerability Trend</h4>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData}>
                <CartesianGrid {...CHART_GRID} />
                <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={false} />
                <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip {...CHART_TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ fontSize: '0.72rem' }} iconSize={12} />
                <Line type="monotone" dataKey="Prod Critical" stroke="#EF4444" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="Prod High" stroke="#F59E0B" strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="Dev High" stroke="#6A6A6A" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {scans && scans.length > 0 && (
        <div className="bg-bg-card border border-border rounded-lg p-5">
          <h4 className="text-[0.85rem] font-semibold mb-4">npm Audit — Dev Dependencies (Non-blocking)</h4>
          <table className="w-full border-collapse text-[0.78rem]">
            <thead>
              <tr>
                {['Severity', 'Count'].map(h => (
                  <th key={h} className="text-left text-[0.62rem] font-semibold uppercase tracking-wider text-text-muted px-3 py-2.5 border-b border-border">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {[
                { label: 'Critical', value: latest!.npm_dev_critical, color: latest!.npm_dev_critical === 0 ? 'text-success' : 'text-danger' },
                { label: 'High', value: latest!.npm_dev_high, color: latest!.npm_dev_high === 0 ? 'text-success' : 'text-warning' },
                { label: 'Moderate', value: latest!.npm_dev_moderate, color: 'text-text-secondary' },
                { label: 'Low', value: latest!.npm_dev_low, color: 'text-text-secondary' },
              ].map(row => (
                <tr key={row.label} className="hover:bg-bg-elevated">
                  <td className="px-3 py-2.5 border-b border-border-subtle text-text-secondary">{row.label}</td>
                  <td className={`px-3 py-2.5 border-b border-border-subtle ${row.color}`}>{row.value}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {(!scans || scans.length === 0) && (
        <div className="text-center py-8 text-text-muted text-sm">No security scans recorded yet.</div>
      )}
    </div>
  );
}

/* ─── Deploys Tab ─── */
function DeploysTab({ deploys }: { deploys?: import('@/lib/types').Deploy[] }) {
  const recentDeploys = deploys ?? [];
  const last30d = recentDeploys.filter(d => {
    const ts = new Date(d.deploy_timestamp).getTime();
    return ts >= Date.now() - 30 * 86_400_000;
  });
  const totalDeploys30d = last30d.length;
  const failedDeploys = last30d.filter(d => !d.success).length;
  const failureRate = totalDeploys30d > 0 ? Math.round((failedDeploys / totalDeploys30d) * 1000) / 10 : 0;
  const successDurations = last30d.filter(d => d.success && d.duration_ms).map(d => d.duration_ms!);
  const medianDuration = successDurations.length > 0
    ? (() => { const sorted = [...successDurations].sort((a, b) => a - b); const mid = Math.floor(sorted.length / 2); return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2; })()
    : null;

  const weeklyData: Record<string, Record<string, number>> = {};
  for (const d of last30d) {
    if (!d.success) continue;
    const date = new Date(d.deploy_timestamp);
    const weekStart = new Date(date);
    weekStart.setDate(date.getDate() - date.getDay());
    const weekLabel = `Wk ${weekStart.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}`;
    if (!weeklyData[weekLabel]) weeklyData[weekLabel] = {};
    weeklyData[weekLabel][d.component] = (weeklyData[weekLabel][d.component] ?? 0) + 1;
  }
  const components = [...new Set(last30d.map(d => d.component))];
  const COMPONENT_COLORS: Record<string, string> = {
    web: 'rgba(0,144,144,0.6)',
    api: 'rgba(34,197,94,0.6)',
    portal: 'rgba(59,130,246,0.5)',
  };
  const chartData = Object.entries(weeklyData).map(([week, counts]) => ({
    week,
    ...counts,
  }));

  return (
    <div>
      <div className="grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4 mb-8">
        <MetricCard label="Deploys (30d)" value={totalDeploys30d} />
        <MetricCard label="Lead Time (median)" value={medianDuration !== null ? formatDuration(medianDuration) : '—'} valueClassName="!text-lg" sub="Target: <1 hour" />
        <MetricCard label="Failure Rate" value={`${failureRate}%`} valueClassName={failureRate < 5 ? 'text-success' : failureRate < 15 ? 'text-warning' : 'text-danger'} sub={`${failedDeploys} rollback${failedDeploys !== 1 ? 's' : ''} / ${totalDeploys30d} deploys`} />
        <MetricCard label="MTTR" value="—" valueClassName="!text-lg" sub="Target: <1 hour" />
      </div>

      {/* Deploy History Chart */}
      {chartData.length > 0 && (
        <div className="bg-bg-card border border-border rounded-lg p-5 mb-6">
          <h4 className="text-[0.85rem] font-semibold mb-4">Deploy History</h4>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid {...CHART_GRID} />
                <XAxis dataKey="week" tick={AXIS_TICK} tickLine={false} axisLine={false} />
                <YAxis tick={AXIS_TICK} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip {...CHART_TOOLTIP_STYLE} />
                <Legend wrapperStyle={{ fontSize: '0.72rem' }} iconSize={12} />
                {components.map(comp => (
                  <Bar
                    key={comp}
                    dataKey={comp}
                    stackId="deploys"
                    fill={COMPONENT_COLORS[comp] ?? 'rgba(148,163,184,0.5)'}
                    radius={[3, 3, 0, 0]}
                  />
                ))}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      <div className="bg-bg-card border border-border rounded-lg p-5">
        <h4 className="text-[0.85rem] font-semibold mb-4">Recent Deploys</h4>
        {recentDeploys.length > 0 ? (
          <table className="w-full border-collapse text-[0.78rem]">
            <thead>
              <tr>
                {['Status', 'Component', 'Commit', 'Branch', 'Duration', 'When'].map(h => (
                  <th key={h} className="text-left text-[0.62rem] font-semibold uppercase tracking-wider text-text-muted px-3 py-2.5 border-b border-border">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {recentDeploys.map(d => (
                <tr key={d.id} className="hover:bg-bg-elevated">
                  <td className="px-3 py-2.5 border-b border-border-subtle text-text-secondary">
                    <span className={`inline-block w-2 h-2 rounded-full mr-1.5 align-middle ${d.success ? 'bg-success' : 'bg-danger'}`} />
                    {d.success ? 'Success' : 'Failed'}
                  </td>
                  <td className="px-3 py-2.5 border-b border-border-subtle text-text-secondary">{d.component}</td>
                  <td className="px-3 py-2.5 border-b border-border-subtle font-mono text-[0.72rem] text-text-secondary">
                    {d.commit_sha?.slice(0, 7) || '—'}
                  </td>
                  <td className="px-3 py-2.5 border-b border-border-subtle font-mono text-[0.72rem] text-text-secondary">{d.branch || '—'}</td>
                  <td className="px-3 py-2.5 border-b border-border-subtle text-text-secondary">{formatDuration(d.duration_ms)}</td>
                  <td className="px-3 py-2.5 border-b border-border-subtle text-text-secondary">{relativeTime(d.deploy_timestamp)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="text-center py-8 text-text-muted text-sm">No deployments recorded yet.</div>
        )}
      </div>
    </div>
  );
}

/* ─── Gates Tab ─── */
function GatesTab({ gates, gateHistory }: { gates?: import('@/lib/types').GateResult[]; gateHistory?: import('@/lib/types').GateHistoryEntry[] }) {
  const [expandedGate, setExpandedGate] = useState<string | null>(null);

  const historyChartData = gateHistory
    ? [...gateHistory].reverse().map((h) => ({
        label: h.run_id.slice(0, 7),
        passed: h.gate_passed,
        total: h.gate_total,
      }))
    : [];

  return (
    <div>
      <div className="grid grid-cols-4 gap-3 mb-8 max-[640px]:grid-cols-2">
        {gates && gates.length > 0 ? gates.map(g => (
          <div key={g.id}>
            <div
              className={`bg-bg-card border rounded-md p-3 text-center cursor-pointer transition-all ${
                expandedGate === g.id
                  ? 'border-accent bg-[rgba(0,144,144,0.12)]'
                  : 'border-border hover:border-text-muted hover:bg-bg-elevated'
              }`}
              onClick={() => setExpandedGate(expandedGate === g.id ? null : g.id)}
              role="button"
              tabIndex={0}
              onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') setExpandedGate(expandedGate === g.id ? null : g.id); }}
            >
              <div className="text-xl mb-1">
                {g.status === 'pass' ? '✓' : g.status === 'fail' ? '✗' : g.status === 'warn' ? '!' : '—'}
              </div>
              <div className={`text-[0.72rem] font-semibold ${g.status === 'pass' ? 'text-success' : g.status === 'fail' ? 'text-danger' : g.status === 'warn' ? 'text-warning' : 'text-text-muted'}`}>
                {g.gate_name}
              </div>
              <div className="text-[0.6rem] text-text-muted mt-0.5">
                {relativeTime(g.run_timestamp)}
              </div>
            </div>

            {expandedGate === g.id && (
              <div className="mt-2 bg-bg-card border border-[rgba(0,144,144,0.25)] rounded-md px-5 py-4 animate-[fadeSlideIn_0.15s_ease-out]">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="text-[0.88rem] font-semibold">{g.gate_name}</h4>
                  <span className={`text-[0.6rem] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${
                    g.status === 'pass'
                      ? 'bg-[rgba(34,197,94,0.1)] text-success'
                      : 'bg-[rgba(239,68,68,0.1)] text-danger'
                  }`}>
                    {g.status}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-8 gap-y-2 text-[0.76rem]">
                  <dt className="text-text-muted font-medium">Status</dt>
                  <dd className="font-medium capitalize">{g.status}</dd>
                  <dt className="text-text-muted font-medium">Last Run</dt>
                  <dd className="font-medium">{relativeTime(g.run_timestamp)}</dd>
                </div>
              </div>
            )}
          </div>
        )) : (
          <div className="col-span-4 text-center py-8 text-text-muted text-sm">No gate results recorded yet.</div>
        )}
      </div>

      {/* Gate History Chart */}
      {historyChartData.length > 1 && (
        <div className="bg-bg-card border border-border rounded-lg p-5">
          <h4 className="text-[0.85rem] font-semibold mb-4">Gate History — Last 10 Runs</h4>
          <div className="h-[200px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={historyChartData}>
                <CartesianGrid {...CHART_GRID} />
                <XAxis dataKey="label" tick={AXIS_TICK} tickLine={false} axisLine={false} />
                <YAxis domain={[0, 8]} tick={AXIS_TICK} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}/8`} />
                <Tooltip {...CHART_TOOLTIP_STYLE} formatter={(value, name) => [name === 'passed' ? `${value}/${historyChartData[0]?.total ?? 8}` : value, 'Gates Passed']} />
                <Bar dataKey="passed" radius={[3, 3, 0, 0]}>
                  {historyChartData.map((entry, index) => (
                    <Cell key={index} fill={entry.passed === entry.total ? 'rgba(34,197,94,0.5)' : 'rgba(245,158,11,0.6)'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
}
